import "../../styles/voice-animation.css";

import React from "react";
import { Box, Typography } from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { tallyTokens } from "../../theme.tsx";
import {
  interruptVoice,
  isVoiceBusy,
  useVoiceStatus,
} from "../../voice/voiceStatus.ts";
import { noticeLabel } from "./voiceLabels.ts";

/** Orb scale while listening, so the user can see the mic is hearing them. */
const LEVEL_GAIN = 1.2;
const MAX_LEVEL_SCALE = 1.18;

/**
 * A running voice session, drawn as one large orb above the composer.
 *
 * State is carried by motion rather than a label: it follows the user's voice
 * while listening, breathes while Tally thinks, and pulses while it speaks.
 * Tapping it while Tally is busy stops the response, as talking over it does.
 * Renders nothing outside voice mode.
 */
const VoiceOrb: React.FC = () => {
  const state = useVoiceStatus((status) => status.state);
  const level = useVoiceStatus((status) => status.level);
  const notice = useVoiceStatus((status) => status.notice);

  if (state === "idle") return null;

  const busy = isVoiceBusy(state);
  const scale =
    state === "listening"
      ? Math.min(1 + level * LEVEL_GAIN, MAX_LEVEL_SCALE)
      : 1;

  const orbClass = [
    "voiceOrb",
    "voiceOrbLarge",
    state === "thinking" || state === "transcribing" ? "voiceOrbThinking" : "",
    state === "speaking" ? "voiceOrbSpeaking" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        bottom: "calc(100% + 40px)",
        transform: "translateX(-50%)",
        // The orb floats over the conversation; only the orb itself is a target.
        pointerEvents: "none",
      }}
    >
      <Box
        component={busy ? "button" : "div"}
        type={busy ? "button" : undefined}
        onClick={busy ? interruptVoice : undefined}
        aria-label={
          busy
            ? i18n._(
                msg({
                  message: "Stop speaking",
                  context: "Voice mode control to interrupt playback",
                }),
              )
            : undefined
        }
        aria-hidden={busy ? undefined : true}
        className={orbClass}
        sx={{
          display: "block",
          width: { xs: 76, sm: 104 },
          height: { xs: 76, sm: 104 },
          p: 0,
          border: 0,
          pointerEvents: "auto",
          cursor: busy ? "pointer" : "default",
          "&:focus-visible": {
            outline: `2px solid ${tallyTokens.brand}`,
            outlineOffset: 4,
          },
        }}
        style={{ transform: `scale(${scale})` }}
      />
      {notice && (
        <Typography
          variant="caption"
          sx={{
            // Under the orb without moving it: the orb is anchored, not stacked.
            position: "absolute",
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            color: tallyTokens.ink2,
            whiteSpace: "nowrap",
            bgcolor: tallyTokens.surface,
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
          }}
        >
          {noticeLabel(notice)}
        </Typography>
      )}
    </Box>
  );
};

export default VoiceOrb;
